import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {debounceTime, Subject, takeUntil, switchMap, map} from 'rxjs';
import {firstValueFrom} from 'rxjs';
import {CommonService} from '../../../../shared/services/common.service';
import {SharedService} from '../../../../shared/core/shared.service';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {ChangeEvent} from '@ckeditor/ckeditor5-angular';
import { ResourcesHubService } from '../../core/resources-hub.service';
import {environment} from '../../../../../../environments/environment';

@Component({
  selector: 'app-add-accordion-form',
  templateUrl: './add-accordion-form.component.html',
  styleUrls: ['./add-accordion-form.component.css']
})
export class AddAccordionFormComponent implements OnChanges, OnInit, OnDestroy {
  @Input() accordionData: any
  @Output() accordionDataEmitter: EventEmitter<any> = new EventEmitter<any>()
  accordionForm: FormGroup = new FormGroup({
    title: new FormControl('', [Validators.required]),
    content: new FormControl('', [Validators.required])
  });
  errorMessages: any = {};
  errorMessageMap = {
    title: {
      required: 'This field is required',
    },
    content: {
      required: 'This field is required',
    }
  }
  public Editor = ClassicEditor;
  editorTextChangeEmitter: Subject<any> = new Subject();
  config
  private readonly onDestroy: Subject<any> = new Subject<any>();

  // Separate property for CKEditor [data] binding — only set from parent, never from editor output
  editorContent: string = '';
  // Track latest editor HTML separately from the form
  private latestEditorHtml: string = '';
  // Flag to ignore ngOnChanges triggered by our own emit
  private selfEmitted = false;

  constructor(
    private commonService: CommonService,
    private resourceService: ResourcesHubService,
    private sharedService: SharedService
  ) {
    const self = this;
    this.config = {
      toolbar: [
        'undo', 'redo',
        '|', 'heading',
        '|', 'fontfamily', 'fontsize', 'fontColor', 'fontBackgroundColor',
        '|', 'bold', 'italic', 'strikethrough', 'subscript', 'superscript',
        'code',
        '|', 'link', 'uploadImage', 'insertTable', 'blockQuote', 'codeBlock',
        '|', 'alignment',
        '|', 'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent'
      ],
      link: {
        addTargetToExternalLinks: true
      },
      image: {},
      table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
      },
      extraPlugins: [function (editor) {
        editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
          return {
            async upload() {
              const file = await loader.file;
              const result$ = self.sharedService
                .getSignedUrlForImageUpload({fileName: file.name, contentType: file.type})
                .pipe(
                  switchMap((res) => self.sharedService.uploadImageToCdn(res?.data?.uploadUrl, file, file.type).pipe(
                    map(() => ({default: `${environment.cdnBaseUrl}${res?.data?.path}`}))
                  ))
                );
              return firstValueFrom(result$);
            },
            abort() {}
          };
        };
      }],
      language: 'en',
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.selfEmitted) {
      this.selfEmitted = false;
      return;
    }
    if (this.accordionData) {
      this.accordionForm.patchValue({
        title: this.accordionData.title,
        content: this.accordionData.content
      }, {emitEvent: false});
      // Only update editorContent when data comes from parent (not from our own editing)
      this.editorContent = this.accordionData.content || '';
      this.latestEditorHtml = this.editorContent;
    }
  }

  ngOnInit(): void {
    this.accordionForm.controls['title'].valueChanges
      .pipe(takeUntil(this.onDestroy), debounceTime(3000)).subscribe(() => {
      this.emitCurrentData();
    });
    this.editorTextChangeEmitter.pipe(takeUntil(this.onDestroy), debounceTime(3000)).subscribe(content => {
      this.latestEditorHtml = content;
      // Update form control for validation only, don't change editorContent (no [data] update)
      this.accordionForm.patchValue({content}, {emitEvent: false});
      this.emitCurrentData();
    })
  }

  private emitCurrentData() {
    if (this.accordionForm.invalid) {
      this.checkForErrors();
      return;
    }
    this.selfEmitted = true;
    this.accordionDataEmitter.emit({
      title: this.accordionForm.value.title,
      content: this.latestEditorHtml
    });
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.accordionForm, this.errorMessageMap, currentField))
    };
  }

  onChange(event: ChangeEvent) {
    if (!event?.editor) return;
    const data = event.editor.data.get();
    this.latestEditorHtml = data;
    // Update form for validation without triggering [data] binding
    this.accordionForm.patchValue({content: data}, {emitEvent: false});
    this.editorTextChangeEmitter.next(data)
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

}
